import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarCheck } from 'lucide-react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/* Probe-Plan (13.10.2026, gesamtabgleich A1): 14 Tage Plan in der App nach dem Probetraining. Der Vertrieb sieht hier,
   wie viel trainiert wurde, und startet ihn von Hand; automatisch nur mit Schalter (Leitung). */

const STATUS = { aktiv: 'läuft', abgelaufen: 'abgelaufen', beendet: 'beendet', umgewandelt: 'Abschluss' };
const tag = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.` : '');
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export default function ProbeplanKarte({ leadId, leitung }) {
  const [d, setD] = useState(null);
  const [e, setE] = useState(null);
  const [vorlage, setVorlage] = useState('studio_start');
  const lade = useCallback(() => {
    api.get(`/probeplan/lead/${leadId}`).then((r) => setD(r.data)).catch(() => setD(null));
    if (leitung) api.get('/probeplan/einstellung').then((r) => setE(r.data)).catch(() => setE(null));
  }, [leadId, leitung]);
  useEffect(() => { lade(); }, [lade]);
  if (!d) return null;
  const p = d.plan;
  const starten = async () => {
    try { await api.post(`/probeplan/lead/${leadId}`, { vorlage }); toast.success('Probe-Plan gestartet – erscheint in der App'); lade(); }
    catch (x) { toast.error(fehlerText(x, 'Nicht gestartet')); }
  };
  const beenden = async () => {
    if (!window.confirm('Probe-Plan beenden?')) return;
    try { await api.delete(`/probeplan/lead/${leadId}`); lade(); } catch (x) { toast.error(fehlerText(x, 'Nicht beendet')); }
  };
  const automatik = async () => {
    try { const r = await api.put('/probeplan/einstellung', { automatisch: !e.automatisch }); setE(r.data); toast.success(r.data.automatisch ? 'Automatisch an' : 'Automatisch aus'); }
    catch (x) { toast.error(fehlerText(x, 'Nicht gespeichert')); }
  };
  return (
    <Card className="border-border/60" data-testid="probeplan-karte">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><CalendarCheck className="h-4 w-4 text-primary" /> Probe-Plan (14 Tage)
          {p && <Badge variant={p.status === 'aktiv' ? 'default' : 'secondary'} className="text-[10px]">{STATUS[p.status] || p.status}</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Nach dem Probetraining: drei Einheiten pro Woche in der App. Vier Tage vor dem Ende kommt die Aufgabe „Abschluss anbieten“.</p>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {p && (
          <>
            <p><b>{p.ist} von {p.soll}</b> Einheiten trainiert{p.letzte && `, zuletzt am ${tag(p.letzte)}`} · {tag(p.start)}–{tag(p.ende)}{p.status === 'aktiv' && ` · noch ${p.tage_rest} Tage`}</p>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (p.ist / Math.max(p.soll, 1)) * 100)}%` }} /></div>
          </>
        )}
        {(!p || p.status !== 'aktiv') && (
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Vorlage" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={vorlage} onChange={(x) => setVorlage(x.target.value)}>
              {Object.entries(d.vorlagen).map(([k, v]) => <option key={k} value={k}>{v.titel}</option>)}
            </select>
            <Button size="sm" onClick={starten}>Probe-Plan starten</Button>
          </div>
        )}
        {p?.status === 'aktiv' && <Button size="sm" variant="ghost" onClick={beenden}>Beenden</Button>}
        {leitung && e && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" className="accent-marke" checked={e.automatisch} onChange={automatik} /> automatisch nach jedem erschienenen Probetraining (Nichtmitglieder)
          </label>
        )}
      </CardContent>
    </Card>
  );
}
